import { useMemo } from "react";
import {
  canonicalWireMaterial,
  formatWireSelection,
  normalizeWireSize,
  ORTHO_WIRE_CUSTOM_VALUE,
  ORTHO_WIRE_MATERIAL_OPTIONS,
  ORTHO_WIRE_NO_CHANGE_VALUE,
  ORTHO_WIRE_NOT_RECORDED_VALUE,
  ORTHO_WIRE_SIZE_OPTIONS,
  wireSelectionsEqual,
} from "./wireRecording";

export { ORTHO_WIRE_CUSTOM_VALUE as ORTHO_WIRE_OTHER_VALUE } from "./wireRecording";

// Material and gauge are independent clinical fields. Clinic catalog values
// are additive only: they can add a size to the presets, never remove the
// standard list or trap a dentist inside a closed dropdown.
export default function OrthoWireGaugeSelect({
  label,
  material = "",
  size = ORTHO_WIRE_NOT_RECORDED_VALUE,
  custom = "",
  options = [],
  previous = null,
  onChange,
  disabled = false,
}) {
  const materials = useMemo(() => {
    const values = new Set(ORTHO_WIRE_MATERIAL_OPTIONS);
    (Array.isArray(options) ? options : []).forEach((option) => {
      const value = canonicalWireMaterial(option.material);
      if (value) values.add(value);
    });
    return Array.from(values);
  }, [options]);

  const sizes = useMemo(() => {
    const values = new Set(ORTHO_WIRE_SIZE_OPTIONS);
    (Array.isArray(options) ? options : []).forEach((option) => {
      const value = normalizeWireSize(option.wire_size);
      if (value) values.add(value);
    });
    if (size && ![ORTHO_WIRE_CUSTOM_VALUE, ORTHO_WIRE_NOT_RECORDED_VALUE].includes(size)) values.add(normalizeWireSize(size));
    return Array.from(values);
  }, [options, size]);

  const current = { material: canonicalWireMaterial(material), size, custom };
  const recorded = !!formatWireSelection(current);
  const carriedForward = recorded && previous && wireSelectionsEqual(current, previous);
  const changed = recorded && previous && !wireSelectionsEqual(current, previous);

  function patch(next) {
    onChange?.({ ...current, ...next });
  }

  function handleMaterial(nextMaterial) {
    if (!nextMaterial) {
      patch({ material: "", size: ORTHO_WIRE_NOT_RECORDED_VALUE, custom: "" });
      return;
    }
    if (nextMaterial === "Other") {
      patch({ material: "Other", size: ORTHO_WIRE_CUSTOM_VALUE });
      return;
    }
    patch({ material: nextMaterial });
  }

  function handleSize(nextSize) {
    if (nextSize === ORTHO_WIRE_NO_CHANGE_VALUE) {
      if (previous) onChange?.(previous);
      return;
    }
    if (nextSize === ORTHO_WIRE_NOT_RECORDED_VALUE) {
      patch({ material: "", size: ORTHO_WIRE_NOT_RECORDED_VALUE, custom: "" });
      return;
    }
    if (nextSize === ORTHO_WIRE_CUSTOM_VALUE) {
      patch({ material: material || "Other", size: ORTHO_WIRE_CUSTOM_VALUE });
      return;
    }
    patch({ material: material || "NiTi", size: nextSize, custom: "" });
  }

  return (
    <div>
      <div className="mb-1 text-xs font-semibold text-slate-500">{label}</div>
      <div className="grid grid-cols-2 gap-2">
        <select
          aria-label={`${label} wire`}
          className="h-9 w-full rounded-lg border border-slate-200 px-2 text-sm disabled:bg-slate-50 disabled:text-slate-500"
          value={canonicalWireMaterial(material)}
          disabled={disabled}
          onChange={(e) => handleMaterial(e.target.value)}
        >
          <option value="">Wire</option>
          {materials.map((value) => <option key={value} value={value}>{value}</option>)}
        </select>
        <select
          aria-label={`${label} wire size`}
          className="h-9 w-full rounded-lg border border-slate-200 px-2 text-sm disabled:bg-slate-50 disabled:text-slate-500"
          value={size || ORTHO_WIRE_NOT_RECORDED_VALUE}
          disabled={disabled}
          onChange={(e) => handleSize(e.target.value)}
        >
          {previous ? <option value={ORTHO_WIRE_NO_CHANGE_VALUE}>No change (use previous)</option> : null}
          <option value={ORTHO_WIRE_NOT_RECORDED_VALUE}>Not recorded</option>
          <optgroup label="Round and rectangular presets">
            {sizes.map((value) => <option key={value} value={value}>{value}</option>)}
          </optgroup>
          <option value={ORTHO_WIRE_CUSTOM_VALUE}>Other / Custom</option>
        </select>
      </div>
      {size === ORTHO_WIRE_CUSTOM_VALUE || material === "Other" ? (
        <input
          aria-label={`${label} custom wire size`}
          className="mt-2 h-9 w-full rounded-lg border border-slate-200 px-2 text-sm outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100 disabled:bg-slate-50 disabled:text-slate-500"
          value={custom || ""}
          disabled={disabled}
          onChange={(e) => patch({ custom: e.target.value, size: ORTHO_WIRE_CUSTOM_VALUE })}
          placeholder="Any size or description, e.g. .016 x .022"
        />
      ) : null}
      <p className={`mt-1 text-[11px] ${changed ? "font-semibold text-amber-700" : "text-slate-400"}`}>
        {changed
          ? `Changed from ${formatWireSelection(previous) || "Not recorded"}`
          : carriedForward
            ? "Carried forward from the previous clinical visit"
            : recorded
              ? "Recorded for this visit"
              : "Not recorded"}
      </p>
    </div>
  );
}
